import { Box, createStyles, Group, Stack } from '@mantine/core';
import { Button } from '@repo/foundations';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useState } from 'react';
import { useLineageFlowContext } from '../../context/LineageFlowProvider/LineageFlowContext';
import type { SearchResult } from '../../hooks/useSearch';
import {
	TEMPORARY_NODE_HEIGHT,
	TEMPORARY_NODE_WIDTH,
} from './TemporaryNode.constants';
import type { LineageFlowTemporaryNode } from './TemporaryNode.types';
import TemporaryNodeEntitySelect from './TemporaryNodeEntitySelect';

interface TemporaryNodeStylesProps {
	selected: boolean;
	position: Position;
}

const useStyles = createStyles(
	(theme, { selected, position }: TemporaryNodeStylesProps) => ({
		wrapper: {
			backgroundColor: theme.other.getColor('surface/primary/default'),
			flexWrap: 'nowrap',
			justifyContent: 'space-between',
			width: TEMPORARY_NODE_WIDTH,
			height: TEMPORARY_NODE_HEIGHT,
			boxShadow: selected
				? `0px 0px 0px 2px ${theme.other.getColor('border/emphasis/default')}`
				: '1px 0px 0px 0px rgba(0, 0, 0, 0.08) inset, -1px 0px 0px 0px rgba(0, 0, 0, 0.08) inset, 0px -1px 0px 0px rgba(0, 0, 0, 0.12) inset, 0px 1px 0px 0px rgba(0, 0, 0, 0.08) inset, 0px 1px 0px 0px rgba(0, 0, 0, 0.05) inset',
			borderRadius: theme.radius.md,
			overflow: 'hidden',
		},
		handle: {
			visibility: 'hidden',
		},
	})
);

function TemporaryNode({
	id,
	selected,
	data,
}: NodeProps<LineageFlowTemporaryNode>) {
	const [selectedEntity, setSelectedEntity] = useState<SearchResult | null>(
		null
	);
	const { classes, cx } = useStyles({
		selected: Boolean(selected),
		position: data.position,
	});

	const { onRemoveTemporaryNode, onSaveTemporaryNode } =
		useLineageFlowContext();

	const handleCancel = () => {
		onRemoveTemporaryNode({ temporaryNodeId: id });
	};

	const handleAdd = () => {
		if (!selectedEntity) return;

		onSaveTemporaryNode({
			temporaryNodeId: id,
			entityId: selectedEntity.id,
		});
	};

	return (
		<Box className={classes.wrapper}>
			<Stack spacing={0}>
				<Handle
					id="TEMP_TARGET"
					className={cx(classes.handle)}
					type="target"
					position={Position.Left}
				/>
				<Stack pt="sm" px="md">
					<TemporaryNodeEntitySelect
						selectedEntity={selectedEntity}
						setSelectedEntity={setSelectedEntity}
					/>
				</Stack>
				<Group position="right" p="md" spacing="xs">
					<Button onClick={handleCancel}>Cancel</Button>
					<Button variant="primary" onClick={handleAdd}>
						Add
					</Button>
				</Group>
				<Handle
					id="TEMP_SOURCE"
					className={cx(classes.handle)}
					type="source"
					position={Position.Right}
				/>
			</Stack>
		</Box>
	);
}

export default TemporaryNode;
